/**
 * Work Landing Page JavaScript
 * Handles scroll animations, parallax, and product carousel
 */

(function() {
  'use strict';

  // -----------------------------------------
  // SCROLL ANIMATIONS (Intersection Observer)
  // -----------------------------------------
  function initScrollAnimations() {
    const fadeElements = document.querySelectorAll('.fade-up');
    
    if (!fadeElements.length) return;
    
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      fadeElements.forEach(el => el.classList.add('is-visible'));
      return;
    }
    
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    };
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);
    
    fadeElements.forEach(el => observer.observe(el));
  }

  // -----------------------------------------
  // PARALLAX EFFECT
  // -----------------------------------------
  function initParallax() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (!parallaxElements.length) return;
    
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;
    
    let ticking = false;
    
    function updateParallax() {
      const scrollY = window.scrollY;
      
      parallaxElements.forEach(el => {
        const speed = parseFloat(el.dataset.parallax) || 0.3;
        const rect = el.getBoundingClientRect();
        const containerTop = rect.top + scrollY;
        const offset = (scrollY - containerTop) * speed;
        
        // Only apply parallax when element is in view
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          el.style.transform = `translateY(${offset}px)`;
        }
      });
      
      ticking = false;
    }
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });
  }

  // -----------------------------------------
  // HERO VIDEO PAUSE/PLAY
  // -----------------------------------------
  function initHeroVideo() {
    const heroVideo = document.querySelector('.work-hero__video');
    
    if (!heroVideo) return;
    
    // Ensure video is muted (required for autoplay)
    heroVideo.muted = true;
    heroVideo.setAttribute('muted', '');
    heroVideo.setAttribute('playsinline', '');
    
    // Try to play immediately
    function attemptPlay() {
      const playPromise = heroVideo.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented, will try again on interaction
        });
      }
    }
    
    // Initial play attempt
    attemptPlay();
    
    // Try again when video can play
    heroVideo.addEventListener('canplay', attemptPlay);
    heroVideo.addEventListener('loadeddata', attemptPlay);
    
    // iOS sometimes needs user interaction - try on first touch/scroll
    let hasTriedInteraction = false;
    function playOnInteraction() {
      if (hasTriedInteraction) return;
      hasTriedInteraction = true;
      attemptPlay();
    }
    
    document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true });
    document.addEventListener('scroll', playOnInteraction, { once: true, passive: true });
    document.addEventListener('click', playOnInteraction, { once: true, passive: true });
    
    // Pause/play based on visibility
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          attemptPlay();
        } else {
          heroVideo.pause();
        }
      });
    }, { threshold: 0.25 });
    
    observer.observe(heroVideo);
  }

  // -----------------------------------------
  // PRODUCT CAROUSEL (simple drag scroll)
  // -----------------------------------------
  function initProductCarousel() {
    const carousel = document.querySelector('[data-product-carousel]');
    
    if (!carousel) return;
    
    const track = carousel.querySelector('.work-products__track');
    
    if (!track) return;
    
    // Touch/swipe support for mobile
    let isDown = false;
    let startX;
    let scrollLeft;
    
    track.addEventListener('mousedown', (e) => {
      isDown = true;
      track.classList.add('is-grabbing');
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });
    
    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.classList.remove('is-grabbing');
    });
    
    track.addEventListener('mouseup', () => {
      isDown = false;
      track.classList.remove('is-grabbing');
    });
    
    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.5;
      track.scrollLeft = scrollLeft - walk;
    });
  }

  // -----------------------------------------
  // STAT COUNTER ANIMATION
  // -----------------------------------------
  function initStatCounters() {
    const stats = document.querySelectorAll('[data-count]');
    
    if (!stats.length) return;
    
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const stat = entry.target;
          const target = parseInt(stat.dataset.count, 10);
          
          if (prefersReducedMotion) {
            stat.textContent = target;
          } else {
            animateCounter(stat, target);
          }
          
          observer.unobserve(stat);
        }
      });
    }, { threshold: 0.5 });
    
    stats.forEach(stat => observer.observe(stat));
  }
  
  function animateCounter(element, target) {
    const duration = 2000;
    const start = 0;
    const startTime = performance.now();
    
    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(start + (target - start) * easeOut);
      
      element.textContent = current;
      
      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        element.textContent = target;
      }
    }
    
    requestAnimationFrame(update);
  }

  // -----------------------------------------
  // BUTTON RIPPLE EFFECT
  // -----------------------------------------
  function initButtonRipples() {
    const buttons = document.querySelectorAll('.work-btn');
    
    buttons.forEach(btn => {
      btn.addEventListener('click', function(e) {
        const rect = this.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const ripple = document.createElement('span');
        ripple.style.cssText = `
          position: absolute;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%);
          pointer-events: none;
          left: ${x}px;
          top: ${y}px;
          animation: ripple 0.6s ease-out forwards;
        `;
        
        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);
        
        setTimeout(() => ripple.remove(), 600);
      });
    });
    
    // Add ripple keyframes if not present
    if (!document.querySelector('#work-ripple-styles')) {
      const style = document.createElement('style');
      style.id = 'work-ripple-styles';
      style.textContent = `
        @keyframes ripple {
          to {
            width: 300px;
            height: 300px;
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  // -----------------------------------------
  // SMOOTH SCROLL FOR ANCHOR LINKS
  // -----------------------------------------
  function initSmoothScroll() {
    const anchors = document.querySelectorAll('.work-landing a[href^="#"]');
    
    anchors.forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  // -----------------------------------------
  // FEATURE ICON TOOLTIPS
  // -----------------------------------------
  function initFeatureTooltips() {
    const featureIcons = document.querySelectorAll('.work-feature-icon');
    
    if (!featureIcons.length) return;
    
    // Create tooltip element
    let tooltip = document.querySelector('.work-feature-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'work-feature-tooltip';
      tooltip.innerHTML = '<div class="work-feature-tooltip__title"></div><div class="work-feature-tooltip__desc"></div>';
      document.body.appendChild(tooltip);
    }
    
    const titleEl = tooltip.querySelector('.work-feature-tooltip__title');
    const descEl = tooltip.querySelector('.work-feature-tooltip__desc');
    
    function showTooltip(icon, e) {
      const title = icon.getAttribute('data-tooltip');
      const desc = icon.getAttribute('data-description');
      
      titleEl.textContent = title;
      descEl.textContent = desc;
      
      // Position tooltip
      const rect = icon.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      
      // Position above the icon
      let top = rect.top - 10;
      let left = rect.left + (rect.width / 2);
      
      // Show first to get dimensions
      tooltip.style.left = left + 'px';
      tooltip.style.top = top + 'px';
      tooltip.style.transform = 'translate(-50%, -100%)';
      tooltip.classList.add('is-visible');
      
      // Adjust if off screen
      const updatedRect = tooltip.getBoundingClientRect();
      if (updatedRect.top < 10) {
        // Show below instead
        top = rect.bottom + 10;
        tooltip.style.top = top + 'px';
        tooltip.style.transform = 'translate(-50%, 0)';
      }
      if (updatedRect.left < 10) {
        tooltip.style.left = '10px';
        tooltip.style.transform = 'translate(0, -100%)';
      }
      if (updatedRect.right > window.innerWidth - 10) {
        tooltip.style.left = (window.innerWidth - 10) + 'px';
        tooltip.style.transform = 'translate(-100%, -100%)';
      }
    }
    
    function hideTooltip() {
      tooltip.classList.remove('is-visible');
    }
    
    featureIcons.forEach(icon => {
      // Desktop hover
      icon.addEventListener('mouseenter', function(e) {
        showTooltip(this, e);
      });
      
      icon.addEventListener('mouseleave', hideTooltip);
      
      // Mobile tap
      icon.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        if (tooltip.classList.contains('is-visible') && this.classList.contains('is-active')) {
          hideTooltip();
          this.classList.remove('is-active');
        } else {
          featureIcons.forEach(i => i.classList.remove('is-active'));
          this.classList.add('is-active');
          showTooltip(this, e);
        }
      });
    });
    
    // Close tooltip when clicking elsewhere
    document.addEventListener('click', function(e) {
      if (!e.target.closest('.work-feature-icon')) {
        hideTooltip();
        featureIcons.forEach(icon => icon.classList.remove('is-active'));
      }
    });
    
    // Hide on scroll
    window.addEventListener('scroll', hideTooltip, { passive: true });
  }

  // -----------------------------------------
  // PRODUCT IMAGE SWIPER
  // -----------------------------------------
  function initProductSwipers() {
    const swipers = document.querySelectorAll('[data-product-swiper]');
    
    if (!swipers.length) return;
    
    swipers.forEach(swiper => {
      const slides = swiper.querySelectorAll('.work-product-card__slide');
      const card = swiper.closest('.work-product-card');
      const dots = card.querySelectorAll('.work-product-card__swiper-dot');
      const prevBtn = card.querySelector('[data-swiper-prev]');
      const nextBtn = card.querySelector('[data-swiper-next]');
      
      if (slides.length <= 1) return;
      
      let currentIndex = 0;
      let startX = 0;
      let startY = 0;
      let isDragging = false;
      let hasMoved = false;
      
      function goToSlide(index) {
        // Wrap around
        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;
        
        currentIndex = index;
        
        // Update slides
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === currentIndex);
        });
        
        // Update dots
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === currentIndex);
        });
      }
      
      // Arrow click navigation
      if (prevBtn) {
        prevBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(currentIndex - 1);
        });
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(currentIndex + 1);
        });
      }
      
      // Dot click navigation
      dots.forEach((dot, i) => {
        dot.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          goToSlide(i);
        });
      });
      
      // Touch/swipe handling
      swiper.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
        hasMoved = false;
      }, { passive: true });
      
      swiper.addEventListener('touchmove', function(e) {
        if (!isDragging) return;
        
        const diffX = e.touches[0].clientX - startX;
        const diffY = e.touches[0].clientY - startY;
        
        // If horizontal swipe is more than vertical, prevent default
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
          hasMoved = true;
        }
      }, { passive: true });
      
      swiper.addEventListener('touchend', function(e) {
        if (!isDragging) return;
        isDragging = false;
        
        const endX = e.changedTouches[0].clientX;
        const diffX = endX - startX;
        
        // Minimum swipe distance
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            goToSlide(currentIndex - 1); // Swipe right = previous
          } else {
            goToSlide(currentIndex + 1); // Swipe left = next
          }
        }
      });
      
      // Mouse drag support for desktop
      swiper.addEventListener('mousedown', function(e) {
        startX = e.clientX;
        isDragging = true;
        hasMoved = false;
        swiper.style.cursor = 'grabbing';
      });
      
      swiper.addEventListener('mousemove', function(e) {
        if (!isDragging) return;
        const diffX = e.clientX - startX;
        if (Math.abs(diffX) > 10) {
          hasMoved = true;
        }
      });
      
      swiper.addEventListener('mouseup', function(e) {
        if (!isDragging) return;
        isDragging = false;
        swiper.style.cursor = '';
        
        const diffX = e.clientX - startX;
        
        if (Math.abs(diffX) > 50) {
          if (diffX > 0) {
            goToSlide(currentIndex - 1);
          } else {
            goToSlide(currentIndex + 1);
          }
        }
      });
      
      swiper.addEventListener('mouseleave', function() {
        isDragging = false;
        swiper.style.cursor = '';
      });
      
      // Prevent link click if swiped
      const link = card.querySelector('.work-product-card__link');
      if (link) {
        link.addEventListener('click', function(e) {
          if (hasMoved) {
            e.preventDefault();
          }
        });
      }
    });
  }

  // -----------------------------------------
  // INITIALIZE
  // -----------------------------------------
  function init() {
    initScrollAnimations();
    initParallax();
    initHeroVideo();
    initProductCarousel();
    initStatCounters();
    initButtonRipples();
    initSmoothScroll();
    initFeatureTooltips();
    initProductSwipers();
  }
  
  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-init on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.work-landing')) {
      init();
    }
  });
  
})();